import { useEffect, useState } from 'react'
import api from '@/lib/api'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { StripePaymentElement, type PaymentElementSuccess } from '@/components/billing/StripePaymentElement'

export type DemoTierSlug = 'associate' | 'partner' | 'principal'

export const CONVERT_TIERS: Array<{ slug: DemoTierSlug; name: string; price: number; tagline: string }> = [
  { slug: 'associate', name: 'Associate', price: 397, tagline: 'Web chat + bookings' },
  { slug: 'partner', name: 'Partner', price: 897, tagline: '24/7 AI calls + chat' },
  { slug: 'principal', name: 'Principal', price: 1497, tagline: 'Callbacks + CRM sync' },
]

interface ConvertDemoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tier: DemoTierSlug
  onSuccess: () => void
}

export function ConvertDemoDialog({ open, onOpenChange, tier, onSuccess }: ConvertDemoDialogProps) {
  const [selectedTier, setSelectedTier] = useState<DemoTierSlug>(tier)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) setSelectedTier(tier)
  }, [open, tier])

  const selected = CONVERT_TIERS.find(t => t.slug === selectedTier) ?? CONVERT_TIERS[1]

  const handlePaymentSuccess = async (result: PaymentElementSuccess) => {
    setConfirming(true)
    setError(null)
    try {
      const res = await api.post('/api/billing/checkout/confirm', {
        setupIntentId: result.setupIntentId,
        paymentMethodId: result.paymentMethodId,
        tier: selectedTier,
        interval: 'monthly',
      })
      if (res.data.success) {
        onSuccess()
      } else {
        throw new Error(res.data.message || 'Subscription creation failed')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Something went wrong')
      setConfirming(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!confirming) onOpenChange(o) }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Subscribe to {selected.name}</DialogTitle>
          <DialogDescription>
            ${selected.price}/mo, billed monthly. Your card is charged immediately —
            features stay exactly the same, just on a paid plan.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {CONVERT_TIERS.map(t => {
            const isSelected = t.slug === selectedTier
            return (
              <button
                key={t.slug}
                type="button"
                disabled={confirming}
                onClick={() => setSelectedTier(t.slug)}
                className={`rounded-lg border p-3 text-left transition-all disabled:opacity-50 ${
                  isSelected
                    ? 'border-brand-periwinkle bg-brand-periwinkle/5 shadow-sm shadow-brand-periwinkle/15'
                    : 'border-border bg-background hover:border-brand-periwinkle/40'
                }`}
              >
                <div className="flex items-baseline justify-between gap-2 sm:block">
                  <p className="text-sm font-semibold">{t.name}</p>
                  <p className="text-base font-heading font-bold tabular-nums sm:mt-0.5">${t.price}<span className="text-[10px] font-normal text-muted-foreground">/mo</span></p>
                </div>
                <p className="text-[11px] sm:text-[10px] text-muted-foreground mt-1 leading-snug">{t.tagline}</p>
              </button>
            )
          })}
        </div>

        <div className="rounded-xl bg-card p-4">
          <StripePaymentElement
            key={selectedTier}
            tier={selectedTier}
            interval="monthly"
            onSuccess={handlePaymentSuccess}
            onError={(msg) => setError(msg)}
            submitLabel={confirming ? 'Activating subscription…' : `Subscribe to ${selected.name} — $${selected.price}/mo`}
          />
        </div>
        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
